import React, {
  ReactElement, useEffect, useState,
} from 'react';
import {
  Button,
  Col,
  FormGroup,
  Heading,
  Label,
  Loader,
  LoaderButton,
  Row,
  Select,
  Text,
} from 'idsk-react-components';
import { PredkladatelInput } from 'slovlex-idsk-react-components';
import { SafeParameterSetter } from '../../../../types/SafeParameterSetter';
import LegislativnyMaterialCreateUpdate from '../../../../model/legislativnyMaterial/LegislativnyMaterialCreateUpdate';
import LegMaterialCreateStepEnum from '../../../../model/legislativnyMaterial/LegMaterialCreateStepEnum';
import CheckDataTable from '../component/CheckDataTable';
import { arrayIncludesInObj, basicAttributeKodList } from '../../../../function/legMaterialFunctions';
import useSearchOrganizacie from '../../../../hooks/useSearchOrganizacie';
import {
  MAX_MULTISELECT_RESULT,
  SHOW_ALL_MAX_RESULT,
} from '../../../../constants/MultiSelectConstant';
import Organizacia from '../../../../model/ciselniky/Organizacia';
import Uuid from '../../../../model/Uuid';
import ZodpovednyPredkladatelComponent from '../component/ZodpovednyPredkladatelComponent';
import useZodpovednyPredkladatelList from '../../../../hooks/useZodpovednyPredkladatelList';
import { useClientFetch } from '../../../../context/ClientStatusContext';
import {
  fetchOrganizaciaLdapPouzivatelMap,
} from '../../../../client/external/ciselniky/organizaciaPouzivatelMapClient';
import convertOrganizaciaPredkladatelListMapToMap
  from '../../../../function/convertOrganizaciaPredkladatelListMapToMap';
import LegislativnyMaterialDetail
  from '../../../../model/legislativnyMaterial/LegislativnyMaterialDetail';

interface Props {
  legMaterialDetail?: LegislativnyMaterialDetail;
  legislativnyMaterial: LegislativnyMaterialCreateUpdate;
  onUpdateField: SafeParameterSetter<LegislativnyMaterialCreateUpdate>;
  onPredkladatelMapUpdate?: (data: Map<string, Array<{ uuid: string, nazov: string }>>) => void;
  predkladatelMap?: Map<string, Array<{ uuid: string, nazov: string }>>;
  loading: boolean;
  getFieldErrorMessage: (fieldName: string) => string;
  onChangeStep: (nextStep: LegMaterialCreateStepEnum) => Promise<void>;
  isUpdate?: boolean;
  isAdminWfe?: boolean;
}

function findOrganizationLdapUuid(selectedOrganizaciaUuid: Uuid, organizaciaList: Organizacia[]): Uuid {
  if (selectedOrganizaciaUuid) {
    return organizaciaList.find(organizacia => organizacia?.uuid === selectedOrganizaciaUuid)?.ldapUuid;
  }
  return null;
}

const PredkladatelStepFour = ({
  legMaterialDetail,
  onUpdateField,
  onPredkladatelMapUpdate,
  predkladatelMap,
  legislativnyMaterial,
  loading,
  getFieldErrorMessage,
  onChangeStep,
  isUpdate = false,
  isAdminWfe = false,
}: Props): ReactElement => {
  const [dalsiPredkladatelMap, setDalsiPredkladatelMap] = useState(predkladatelMap);
  const [organizaciaSearch, setOrganizaciaSearch] = useState<string>('');
  const [limit, setLimit] = useState(MAX_MULTISELECT_RESULT);
  const dalsiPredklLength = legislativnyMaterial?.dalsiPredkladatelia?.length;
  const [selectedOrganizacia, setSelectedOrganizacia] = useState<{ uuid: string, nazov: string }>(
    dalsiPredklLength > 0 ? legislativnyMaterial.dalsiPredkladatelia[dalsiPredklLength - 1]?.organizacia : undefined,
  );
  const [reloadDalsiPredkladatelData, setReloadDalsiPredkladatelData] = useState(true);
  const [organizationLdapUuid, setOrganizationLdapUuid] = useState<Uuid>(undefined);
  const [reloadZodpPrekladatel, setReloadZodpPrekladatel] = useState(true);

  const {
    data: organizaciaPredkladatelListMap,
    status: organizaciaPredkladatelListStatus,
  } = useClientFetch(() => fetchOrganizaciaLdapPouzivatelMap(
    [...legislativnyMaterial.dalsiPredkladatelia.map(predkladatel => predkladatel.organizacia?.uuid),
      legislativnyMaterial.zodpovednaInstitucia?.uuid],
  ), []);

  const {
    pouzivatelia: zodpovednyPredkladatelList,
    pouzivateliaStatus: zodpovednyPredkladatelStatus,
  } = useZodpovednyPredkladatelList(
    reloadZodpPrekladatel,
    setReloadZodpPrekladatel,
    legislativnyMaterial.zodpovednaInstitucia?.ldapUuid,
  );

  const {
    organizacie: organizaciaListData,
    organizacieStatus,
  } = useSearchOrganizacie({
    typ: undefined,
    ids: [],
    ldap: true,
    legislativna: true,
    platna: true,
    nazov: selectedOrganizacia?.nazov || organizaciaSearch,
    limit,
  });

  const organizaciaList = organizaciaListData?.map(organizacia => ({
    uuid: organizacia?.uuid,
    nazov: organizacia?.nazov,
  }));

  const {
    pouzivatelia: dalsiPredkladatelListData,
    pouzivateliaStatus,
  } = useZodpovednyPredkladatelList(
    reloadDalsiPredkladatelData,
    setReloadDalsiPredkladatelData,
    organizationLdapUuid,
  );

  useEffect(() => {
    if (organizaciaPredkladatelListStatus?.finished && !organizaciaPredkladatelListStatus.loading) {
      onPredkladatelMapUpdate(convertOrganizaciaPredkladatelListMapToMap(organizaciaPredkladatelListMap));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizaciaPredkladatelListMap, organizaciaPredkladatelListStatus]);

  useEffect(() => {
    if (selectedOrganizacia) {
      setOrganizationLdapUuid(findOrganizationLdapUuid(selectedOrganizacia?.uuid, organizaciaListData));
      if (!dalsiPredkladatelMap.has(selectedOrganizacia?.uuid)) {
        setReloadDalsiPredkladatelData(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizaciaListData, selectedOrganizacia]);

  useEffect(() => {
    if (pouzivateliaStatus?.finished && !pouzivateliaStatus.loading) {
      if (!dalsiPredkladatelMap.has(selectedOrganizacia?.uuid)) {
        const dalsiPredkladatelList = dalsiPredkladatelListData?.map(predkladatel => ({
          uuid: predkladatel?.uuid,
          nazov: predkladatel?.nazov,
        }));
        const updatedMap = dalsiPredkladatelMap;
        updatedMap.set(selectedOrganizacia?.uuid, dalsiPredkladatelList);
        setDalsiPredkladatelMap(updatedMap);
        onPredkladatelMapUpdate(updatedMap);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dalsiPredkladatelListData, dalsiPredkladatelMap, pouzivateliaStatus]);

  return (
    <div>
      <Heading className="page-title" size="xl">
        {isUpdate ? 'Úprava predkladateľov' : 'Vyplňte predkladateľov'}
      </Heading>
      <div className="institution-container">
        {isAdminWfe && isUpdate
          ? (
            <ZodpovednyPredkladatelComponent
              legMaterialDetail={legMaterialDetail}
              selectedItem={{
                organizacia: legislativnyMaterial.zodpovednaInstitucia,
                predkladatel: legislativnyMaterial.zodpovednyPredkladatel,
              }}
              setSelectedItem={(newSelectedItem) => {
                onUpdateField('zodpovednaInstitucia', newSelectedItem.organizacia);
                onUpdateField('zodpovednyPredkladatel', newSelectedItem.predkladatel);
              }}
              getFieldErrorMessage={getFieldErrorMessage}
              predkladatelInitMap={predkladatelMap}
            />
          )
          : (
            <>
              <Row className="institution-label">
                <Col>
                  <Text type="span">Zodpovedná inštitúcia</Text>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Text type="span">{legislativnyMaterial.zodpovednaInstitucia?.uplnyNazov}</Text>
                </Col>
              </Row>
            </>
          )}
      </div>
      {(!isAdminWfe || !isUpdate) && (
        <Loader loading={zodpovednyPredkladatelStatus.loading}>
          <FormGroup>
            <Label htmlFor="zodpovednyPredkladatel">Zodpovedný predkladateľ</Label>
            <Select
              id="zodpovednyPredkladatel"
              width="two-thirds"
              name="zodpovednyPredkladatel"
              value={legislativnyMaterial.zodpovednyPredkladatel?.uuid}
              onChange={({ target }) => {
                const selectedValue = zodpovednyPredkladatelList
                  ?.find(predkladatel => predkladatel?.uuid === target.value);
                onUpdateField('zodpovednyPredkladatel', selectedValue);
              }}
              error={getFieldErrorMessage('zodpovednyPredkladatel')}
            >
              <option key="Vyberte" value="" hidden>Vyberte</option>
              {(zodpovednyPredkladatelList && zodpovednyPredkladatelList?.length > 0) ? (
                <>
                  {zodpovednyPredkladatelList?.map(predkladatel => (
                    <option
                      key={predkladatel?.uuid}
                      value={predkladatel?.uuid}
                    >
                      {predkladatel?.nazov}
                    </option>
                  ))}
                </>
              ) : (
                <option key="emptyOption" value="emptyOption" disabled>
                  Nenašiel sa žiadny záznam.
                </option>
              )}
            </Select>
          </FormGroup>
        </Loader>
      )}
      {organizaciaListData && (
        <PredkladatelInput
          loadingOrganizacia={organizacieStatus.loading}
          initialValues={legMaterialDetail ? legMaterialDetail.dalsiPredkladatelia : []}
          handleShowAll={() => {
            setLimit(SHOW_ALL_MAX_RESULT);
          }}
          withShowAllButton={(limit === MAX_MULTISELECT_RESULT
              && organizaciaList.length === MAX_MULTISELECT_RESULT)
            || (limit === SHOW_ALL_MAX_RESULT && organizaciaList.length <= MAX_MULTISELECT_RESULT)}
          searchText={organizaciaSearch}
          setSearchText={(text) => {
            setOrganizaciaSearch(text);
            setLimit(MAX_MULTISELECT_RESULT);
          }}
          className="create-leg-material__zodpovedni-predkladatelia"
          label="Ďalší predkladatelia"
          required={false}
          hint="Vyberte organizáciu a následne z ponúknutých osôb. Je možné pridať viacero ďalších predkladateľov."
          organizaciaData={organizaciaList}
          loadingPredkladatel={pouzivateliaStatus.loading}
          predkladatelData={dalsiPredkladatelMap}
          selectedItemList={legislativnyMaterial.dalsiPredkladatelia}
          setSelectedItemList={((itemList) => {
            setSelectedOrganizacia(itemList[itemList.length - 1].organizacia);
            onUpdateField('dalsiPredkladatelia', itemList);
          })}
          width="full"
        />
      )}
      <LoaderButton
        className="legislativny-material-create__submit-button"
        loading={loading}
        type="submit"
        size="large"
        onClick={() => onChangeStep(LegMaterialCreateStepEnum.CHECK_DATA_5)}
        disableOutsideClickWhileLoading
      >Pokračovať
      </LoaderButton>
      <Button
        className="legislativny-material__button"
        type="button"
        buttonStyle="third"
        size="large"
        disabled={loading}
        onClick={() => {
          onChangeStep((
            arrayIncludesInObj(basicAttributeKodList, legislativnyMaterial.typ?.kod))
            ? LegMaterialCreateStepEnum.BASIC_DATA_2 : LegMaterialCreateStepEnum.RELATED_DATA_3);
        }}
      >
        Späť
      </Button>
      <Heading size="m">Vaše odpovede</Heading>
      <CheckDataTable
        onChangeStep={onChangeStep}
        isUpdate={isUpdate}
        legislativnyMaterial={legislativnyMaterial}
      />
    </div>
  );
};

export default PredkladatelStepFour;
